import React, { Component } from 'react';
import { browserHistory, withRouter } from 'react-router';
import { connect } from 'react-redux';

import { MdWarning as WarningIcon } from 'react-icons/md';
import { MdCheckCircle as CheckIcon } from 'react-icons/md';

import LoginLoading from './LoginLoading';
import LoginEmailPassword from './LoginEmailPassword';
import LoginValidateMobileNumber from './LoginValidateMobileNumber';
import LoginAlternative from './LoginAlternative';
import LoginVerificationCode from './LoginVerificationCode';
import LandlordLogo from 'components/shared/LandlordLogo';

import { AccentColor } from 'helpers/AccentColorHelper';
import { hostIsConcert } from 'helpers/CurrentWindowLocation';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';

import {
  StyledSSOButton,
  StyledCard,
  StyledCardContent,
  StyledButton,
  StyledLoginMessageH2,
} from 'helpers/StyledLoginComponentsHelper';

import constants from 'config/constants';
import { titleize } from 'helpers/Formatter';
import msIcon from 'assets/microsoft-sso.png';
import oktaIcon from 'assets/okta-sso.png';

let Login = connect(
  (store) => ({
    showLoginForm: store.authentication.showLoginForm,
    loginErrorCode: store.authentication.loginErrorCode,
    loginErrorData: store.authentication.loginErrorData,
    showLoginDialog: store.authentication.showLoginDialog,
    loginMessage: store.userTypes.loginMessage,
    passwordResetURL: store.userTypes.passwordResetURL,
    email: store.userTypes.email,
    createdSuccess: store.userTypes.createdSuccess,
    landlordSSOEnabled: store.userTypes.landlordSSOEnabled,
    uuid: store.userTypes.uuid,
    propertyTypes: store.userTypes.propertyTypes,
    userType: store.userTypes.userType,
    ssoProvidersLandlord: store.userTypes.ssoProvidersLandlord,
    ssoProvidersAgent: store.userTypes.ssoProvidersAgent,
    verbiages: store.userTypes.verbiages,
  }),
  {}
)(
  class Login extends Component {
    getTranslatedLoginMessage = () => {
      const { propertyTypes, verbiages, loginMessage } = this.props;
      const isCommercial = propertyTypes === 'commercial';

      const residentLoginFromVerbiage =
        verbiages && verbiages['residentLogin'] && verbiages['residentLogin'][getCurrentLocale()];
      const landlordLoginFromVerbiage =
        verbiages && verbiages['landlordLogin'] && verbiages['landlordLogin'][getCurrentLocale()];

      if (loginMessage === 'residentLoginPortal' && (isCommercial || hostIsConcert())) {
        return getTranslatedString('tenantLoginPortal');
      } else if (loginMessage === 'residentLoginPortal' && residentLoginFromVerbiage) {
        return residentLoginFromVerbiage;
      } else if (loginMessage === 'landlordLoginPortal' && landlordLoginFromVerbiage) {
        return landlordLoginFromVerbiage;
      }

      return getTranslatedString(loginMessage);
    };

    render() {
      const { isButtonEnabled } = this.props.details;
      const { loginErrorCode, loginErrorData, showLoginForm, createdSuccess, isAuthenticating } =
        this.props;
      if (isAuthenticating) {
        return <CardLoading />;
      }

      const ssoEnabled = this.props.landlordSSOEnabled === true && this.props.userType === 'agent';
      let ssoProviders = [];
      if (ssoEnabled) {
        ssoProviders = this.props.ssoProvidersLandlord || this.props.ssoProvidersAgent;
      }

      const showPasswordExpired = loginErrorCode === 'passwordExpired' && showLoginForm;
      const showVerifyMobileNumber = loginErrorCode === 'verifyMobileNumber' && showLoginForm;
      const showVerificationCode = loginErrorCode === 'verificationCode' && showLoginForm;
      const showAlternative = loginErrorCode === 'alternative' && showLoginForm;
      const showEmailPassword =
        showLoginForm &&
        this.props.details &&
        !(showPasswordExpired || showVerifyMobileNumber || showVerificationCode || showAlternative);

      const contentFullWidth = showVerifyMobileNumber;
      return (
        <StyledCard>
          <StyledCardContent contentFullWidth={contentFullWidth}>
            <LandlordLogo
              fontSize="90px"
              maxWidth="273px"
              height="100px"
              textHeight="70px"
              textAlign="left"
            />
            <StyledLoginMessageH2 aria-level="1">
              {getTranslatedString('login.welcome')}
              <br />
              {this.getTranslatedLoginMessage()}
            </StyledLoginMessageH2>
            {showEmailPassword && loginErrorCode && (
              <div className="error-message" id="error-message" data-testid="login-error-message">
                <WarningIcon /> {getTranslatedString(loginErrorCode, loginErrorData)}
              </div>
            )}

            {showEmailPassword && createdSuccess && (
              <div className="success-message" id={'success-message'}>
                <CheckIcon /> {getTranslatedString('login.signupSuccess')}
              </div>
            )}
            {ssoEnabled &&
              ssoProviders.map((ssoProvider) => {
                return (
                  <SSOLoginButton
                    loginMessage={this.props.loginMessage}
                    SSOLoginUrl={`${constants.BASE_API_URL}/v1/saml/sso?landlord_id=${
                      this.props.uuid
                    }&provider=${ssoProvider}&lang=${getCurrentLocale()}`}
                    isButtonEnabled={isButtonEnabled}
                    ssoProvider={ssoProvider}
                  />
                );
              })}
            {ssoEnabled && (
              <div className="row" style={{ textAlign: 'center' }}>
                <h5 aria-level="1" style={{ padding: '10px 0' }}>
                  {getTranslatedString('landlordPortal.login.or')}
                </h5>
              </div>
            )}
            {showEmailPassword && (
              <LoginEmailPassword
                details={this.props.details}
                passwordResetURL={this.props.passwordResetURL}
                handleInputChange={this.props.handleInputChange}
                login={this.props.login}
                detectEnterKeyDown={this.props.detectEnterKeyDown}
              />
            )}
            {showPasswordExpired && (
              <ResetPassword passwordResetURL={this.props.passwordResetURL} />
            )}
            {showVerifyMobileNumber && (
              <LoginValidateMobileNumber
                details={this.props.details}
                handleInputChange={this.props.handleMobileNumberChange}
                handleVerifyNumber={this.props.handleVerifyNumber}
                detectEnterKeyDown={this.props.detectEnterKeyDown}
                loginErrorData={this.props.loginErrorData}
              />
            )}
            {showVerificationCode && (
              <LoginVerificationCode
                handleInputChange={this.props.handleInputChange}
                login={this.props.login}
                resetVerificationCode={this.props.resetVerificationCode}
                detectEnterKeyDown={this.props.detectEnterKeyDown}
                showAlternative={this.props.showAlternative}
                details={this.props.details}
                loginErrorData={this.props.loginErrorData}
              />
            )}
            {showAlternative && (
              <LoginAlternative
                changeVerificationMethod={this.props.changeVerificationMethod}
                details={this.props.details}
              />
            )}
          </StyledCardContent>
        </StyledCard>
      );
    }
  }
);

const CardLoading = () => (
  <StyledCard>
    <StyledCardContent>
      <LoginLoading inCard />
    </StyledCardContent>
  </StyledCard>
);

const ResetPassword = ({ passwordResetURL }) => (
  <StyledButton
    accentColor={AccentColor()}
    onClick={() => browserHistory.push(`/${passwordResetURL}`)}
  >
    {getTranslatedString('resetPassword')}
  </StyledButton>
);

const SSOLoginButton = ({ SSOLoginUrl, isButtonEnabled, ssoProvider }) => {
  return (
    <StyledSSOButton
      name="sign_in_with_sso"
      primary={true}
      onClick={() => (window.location = SSOLoginUrl)}
      disabled={!isButtonEnabled}
      labelStyle={{ fontSize: '18px' }}
      variant="outlined"
    >
      <SSOIcon provider={ssoProvider} /> {getTranslatedString(`signInWith${titleize(ssoProvider)}`)}
    </StyledSSOButton>
  );
};

const SSOIcon = ({ provider }) => {
  if (provider === 'azure') {
    return (
      <img
        alt="microsoft sso login button"
        style={{ width: '35px', paddingRight: '5px' }}
        src={msIcon}
      />
    );
  }
  if (provider === 'okta') {
    return (
      <img
        alt="okta sso login button"
        style={{ width: '28px', paddingRight: '7px' }}
        src={oktaIcon}
      />
    );
  }

  return null;
};

export default withRouter(Login);
